import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  BarChart3, Heart, Eye, TrendingUp, Users, Tag, Package, 
  Calendar, ArrowUp, ArrowDown, DollarSign, ShoppingBag, AlertTriangle 
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend 
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'summary', label: 'Günlük Özet', icon: Calendar },
  { id: 'sales', label: 'Satışlar', icon: BarChart3 },
  { id: 'favorites', label: 'Favoriler', icon: Heart },
  { id: 'visits', label: 'Ziyaretler', icon: Eye },
  { id: 'funnel', label: 'Dönüşüm Hunisi', icon: TrendingUp },
  { id: 'revenue', label: 'Top Ciro', icon: DollarSign },
  { id: 'customers', label: 'VIP Müşteriler', icon: Users },
  { id: 'discounts', label: 'İndirim Kodu', icon: Tag },
  { id: 'stock', label: 'Stok Uyarıları', icon: AlertTriangle },
];

const AdminReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const token = localStorage.getItem('adminToken');
  
  useEffect(() => {
    if (!token) navigate('/admin/login');
  }, []);

  const authHeaders = { 'Authorization': `Bearer ${token}` };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3 mb-6">
          <BarChart3 className="text-red-600" /> Raporlar & Analitik
        </h1>

        {/* Tab navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto">
          <div className="flex min-w-max border-b">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`report-tab-${t.id}`}
                  className={`flex items-center gap-2 px-5 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === t.id
                      ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                      : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div>
          {activeTab === 'summary' && <DailySummary authHeaders={authHeaders} />}
          {activeTab === 'sales' && <SalesReport authHeaders={authHeaders} />}
          {activeTab === 'favorites' && <FavoritesReport authHeaders={authHeaders} />}
          {activeTab === 'visits' && <VisitsReport authHeaders={authHeaders} />}
          {activeTab === 'funnel' && <FunnelReport authHeaders={authHeaders} />}
          {activeTab === 'revenue' && <TopRevenueReport authHeaders={authHeaders} />}
          {activeTab === 'customers' && <TopCustomersReport authHeaders={authHeaders} />}
          {activeTab === 'discounts' && <DiscountReport authHeaders={authHeaders} />}
          {activeTab === 'stock' && <StockReport authHeaders={authHeaders} />}
        </div>
      </div>
    </AdminLayout>
  );
};

const useReport = (endpoint, authHeaders, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}${endpoint}`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, deps);
  return { data, loading };
};

const fmtTL = (n) => `${(n || 0).toLocaleString('tr-TR')} TL`;

// ========== DAILY SUMMARY ==========
const DailySummary = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/daily-summary', authHeaders, []);
  const timeline = useReport('/api/admin/reports/sales-timeline?days=30', authHeaders, []);

  if (loading) return <Loading />;
  if (!data) return <Empty />;

  const Card = ({ label, today, change, suffix = '', prefix = '' }) => {
    const up = change > 0;
    const down = change < 0;
    return (
      <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-red-500">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-800 mt-1">{prefix}{today}{suffix}</p>
        <p className={`text-xs mt-2 flex items-center gap-1 ${up ? 'text-green-600' : down ? 'text-red-600' : 'text-gray-500'}`}>
          {up && <ArrowUp size={12} />}
          {down && <ArrowDown size={12} />}
          {change > 0 ? '+' : ''}{change}% dünden
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Bugün Sipariş" today={data.today.orders} change={data.changes.orders} />
        <Card label="Bugün Ciro" today={fmtTL(data.today.revenue)} change={data.changes.revenue} />
        <Card label="Bugün Ziyaret" today={data.today.visits} change={data.changes.visits} />
        <Card label="Yeni Üye" today={data.today.new_users} change={data.changes.new_users} />
      </div>

      {/* 30-day revenue chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp size={18} /> Son 30 Gün Ciro & Sipariş Trendi
        </h3>
        {timeline.data?.timeline?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline.data.timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="revenue" name="Ciro (TL)" stroke="#dc2626" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="orders" name="Sipariş Sayısı" stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        ) : <p className="text-center text-gray-500 py-12">Henüz veri yok</p>}
      </div>
    </div>
  );
};

// ========== SALES ==========
const SalesReport = ({ authHeaders }) => {
  const [period, setPeriod] = useState('weekly');
  const { data, loading } = useReport(`/api/admin/reports/sales?period=${period}`, authHeaders, [period]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <BarChart3 size={18} /> Hangi Üründen Kaç Satıldı
        </h3>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[{k:'daily',l:'Günlük'},{k:'weekly',l:'Haftalık'},{k:'monthly',l:'Aylık'}].map(p => (
            <button key={p.k} onClick={() => setPeriod(p.k)}
              data-testid={`sales-period-${p.k}`}
              className={`px-4 py-1.5 text-sm font-medium rounded ${period===p.k?'bg-white shadow text-red-600':'text-gray-600'}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>
      {loading ? <Loading /> : !data?.sales?.length ? <Empty /> : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.sales.slice(0, 10).map(s => ({
              name: s.product.name.slice(0, 25),
              adet: s.units_sold,
              ciro: s.revenue
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="adet" fill="#dc2626" name="Adet" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-xs text-gray-700">
                <tr>
                  <th className="p-2">Ürün</th>
                  <th className="p-2 text-right">Adet</th>
                  <th className="p-2 text-right">Ciro</th>
                  <th className="p-2 text-right">Sipariş</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map(s => (
                  <tr key={s.product.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">
                      <div className="flex items-center gap-3">
                        <img src={s.product.image} alt="" className="w-10 h-10 rounded object-cover" />
                        <div>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono mr-2">#{s.product.id}</span>
                          <span className="text-sm">{s.product.name}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-right font-semibold">{s.units_sold}</td>
                    <td className="p-2 text-right text-green-700 font-semibold">{fmtTL(s.revenue)}</td>
                    <td className="p-2 text-right">{s.order_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ========== FAVORITES ==========
const FavoritesReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/favorites', authHeaders, []);

  if (loading) return <Loading />;
  if (!data?.favorites?.length) return <Empty />;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Heart size={18} className="text-red-500" /> Favoriye Alınan Ürünler ({data.total_products_favorited})
      </h3>
      <div className="space-y-3">
        {data.favorites.map(f => (
          <div key={f.product.id} className="border rounded-lg p-4 hover:bg-gray-50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3 flex-1">
                <img src={f.product.image} alt="" className="w-14 h-14 rounded object-cover" />
                <div className="flex-1">
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono">#{f.product.id}</span>
                  <p className="font-medium text-sm mt-1">{f.product.name}</p>
                  <p className="text-xs text-gray-500">{fmtTL(f.product.price)}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold">
                  <Heart size={14} /> {f.count} favori
                </span>
              </div>
            </div>
            {f.emails.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500 mb-1">Favoriye alan üyeler:</p>
                <div className="flex flex-wrap gap-2">
                  {f.emails.map((e, i) => (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{e}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== VISITS ==========
const VisitsReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/visits', authHeaders, []);
  if (loading) return <Loading />;
  if (!data?.visits?.length) return <Empty msg="Henüz ürün ziyareti kaydedilmemiş" />;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Eye size={18} /> Ürün Ziyaret Raporu ({data.total_products_visited} ürün)
      </h3>
      <div className="space-y-3">
        {data.visits.map(v => (
          <div key={v.product.id} className="border rounded-lg p-4 hover:bg-gray-50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3 flex-1">
                <img src={v.product.image} alt="" className="w-14 h-14 rounded object-cover" />
                <div className="flex-1">
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono">#{v.product.id}</span>
                  <p className="font-medium text-sm mt-1">{v.product.name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                  {v.total_visits} ziyaret
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1 text-purple-700">
                <Users size={12} /> Üye: <strong>{v.member_visits}</strong>
              </span>
              <span className="flex items-center gap-1 text-gray-600">
                Misafir: <strong>{v.guest_visits}</strong>
              </span>
            </div>
            {v.member_emails.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Ziyaret eden üyeler:</p>
                <div className="flex flex-wrap gap-1.5">
                  {v.member_emails.map((e, i) => (
                    <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">{e}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== FUNNEL ==========
const FunnelReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/funnel', authHeaders, []);
  if (loading) return <Loading />;
  if (!data) return <Empty />;

  const steps = [
    { label: 'Ziyaret', value: data.visits, color: '#3b82f6' },
    { label: 'Favori', value: data.favorites, color: '#f59e0b' },
    { label: 'Sepet', value: data.abandoned_carts + data.orders, color: '#8b5cf6' },
    { label: 'Sipariş', value: data.orders, color: '#16a34a' },
  ];
  const max = Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-6 flex items-center gap-2">
        <TrendingUp size={18} /> Dönüşüm Hunisi
      </h3>
      <div className="space-y-3 mb-6">
        {steps.map(s => (
          <div key={s.label}>
            <div className="flex justify-between mb-1">
              <span className="font-medium text-gray-700">{s.label}</span>
              <span className="font-bold">{s.value.toLocaleString('tr-TR')}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
              <div className="h-8 flex items-center px-3 text-white text-sm font-medium transition-all"
                style={{ width: `${(s.value / max) * 100}%`, backgroundColor: s.color, minWidth: '2rem' }}>
                {Math.round((s.value / max) * 100)}%
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded p-4">
          <p className="text-xs text-gray-600">Ziyaret → Favori</p>
          <p className="text-2xl font-bold text-blue-700">%{data.visit_to_favorite_rate}</p>
        </div>
        <div className="bg-purple-50 rounded p-4">
          <p className="text-xs text-gray-600">Ziyaret → Sipariş</p>
          <p className="text-2xl font-bold text-purple-700">%{data.visit_to_order_rate}</p>
        </div>
        <div className="bg-green-50 rounded p-4">
          <p className="text-xs text-gray-600">Sepet → Sipariş</p>
          <p className="text-2xl font-bold text-green-700">%{data.cart_to_order_rate}</p>
        </div>
      </div>
    </div>
  );
};

// ========== TOP REVENUE ==========
const TopRevenueReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/top-revenue', authHeaders, []);
  if (loading) return <Loading />;
  if (!data?.products?.length) return <Empty />;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <DollarSign size={18} className="text-green-600" /> En Çok Ciro Getiren Ürünler
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.products.slice(0, 10).map(p => ({
          name: p.product.name.slice(0, 20),
          ciro: p.revenue
        }))}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={70} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => fmtTL(v)} />
          <Bar dataKey="ciro" fill="#16a34a" name="Ciro" />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4">
        {data.products.map((p, i) => (
          <div key={p.product.id} className="flex items-center gap-3 p-3 border-b hover:bg-gray-50">
            <span className="w-8 h-8 rounded-full bg-red-100 text-red-700 font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <img src={p.product.image} alt="" className="w-12 h-12 rounded object-cover" />
            <div className="flex-1">
              <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono mr-2">#{p.product.id}</span>
              <span className="text-sm">{p.product.name}</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-green-700">{fmtTL(p.revenue)}</p>
              <p className="text-xs text-gray-500">{p.units} adet</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== TOP CUSTOMERS ==========
const TopCustomersReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/top-customers', authHeaders, []);
  if (loading) return <Loading />;
  if (!data?.customers?.length) return <Empty />;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Users size={18} /> VIP Müşteriler - En Çok Harcayanlar
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs text-gray-700">
            <tr>
              <th className="p-3">#</th>
              <th className="p-3">Müşteri</th>
              <th className="p-3 text-right">Toplam</th>
              <th className="p-3 text-right">Sipariş</th>
              <th className="p-3 text-right">Ort. Sepet</th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((c, i) => (
              <tr key={c.email} className="border-t hover:bg-gray-50">
                <td className="p-3">
                  <span className="w-7 h-7 rounded-full bg-red-100 text-red-700 font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                </td>
                <td className="p-3">
                  <p className="font-medium text-sm">{c.name || c.email}</p>
                  <p className="text-xs text-gray-500">{c.email}</p>
                </td>
                <td className="p-3 text-right font-bold text-green-700">{fmtTL(c.total_spent)}</td>
                <td className="p-3 text-right">{c.order_count}</td>
                <td className="p-3 text-right text-gray-600">{fmtTL(c.avg_order)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== DISCOUNTS ==========
const DiscountReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/discount-performance', authHeaders, []);
  if (loading) return <Loading />;
  if (!data?.codes?.length) return <Empty msg="Henüz indirim kodu yok" />;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Tag size={18} /> İndirim Kodu Performansı
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs text-gray-700">
            <tr>
              <th className="p-3">Kod</th>
              <th className="p-3">%</th>
              <th className="p-3 text-right">Sipariş</th>
              <th className="p-3 text-right">Ciro Getirdi</th>
              <th className="p-3 text-right">İndirim Verildi</th>
              <th className="p-3">Durum</th>
            </tr>
          </thead>
          <tbody>
            {data.codes.map(c => (
              <tr key={c.code} className="border-t hover:bg-gray-50">
                <td className="p-3 font-mono font-bold text-red-600">{c.code}</td>
                <td className="p-3">%{c.percent}</td>
                <td className="p-3 text-right">{c.order_count}</td>
                <td className="p-3 text-right font-bold text-green-700">{fmtTL(c.revenue_generated)}</td>
                <td className="p-3 text-right text-orange-700">-{fmtTL(c.discount_given)}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {c.active ? 'Aktif' : 'Pasif'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== STOCK ==========
const StockReport = ({ authHeaders }) => {
  const { data, loading } = useReport('/api/admin/reports/stock-alerts', authHeaders, []);
  if (loading) return <Loading />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-semibold text-orange-700 mb-4 flex items-center gap-2">
          <AlertTriangle size={18} /> Stok Azalan ({data?.low_stock_count || 0})
        </h3>
        {!data?.low_stock?.length ? <p className="text-sm text-gray-500">Stok uyarısı yok ✅</p> : (
          <div className="space-y-2">
            {data.low_stock.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-2 border-l-4 border-orange-400 bg-orange-50 rounded">
                <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" />
                <div className="flex-1">
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono mr-1">#{p.id}</span>
                  <span className="text-sm">{p.name}</span>
                </div>
                <span className="text-orange-700 font-bold text-sm">Stok: {p.stock ?? 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
          <Package size={18} /> Ölü Stok - 30 gündür satılmamış ({data?.dead_stock_count || 0})
        </h3>
        {!data?.dead_stock?.length ? <p className="text-sm text-gray-500">Harika, hepsi satılıyor!</p> : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.dead_stock.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-2 border-l-4 border-red-400 bg-red-50 rounded">
                <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" />
                <div className="flex-1">
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono mr-1">#{p.id}</span>
                  <span className="text-sm">{p.name}</span>
                </div>
                <span className="text-gray-600 text-xs">{fmtTL(p.price)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Loading = () => <div className="text-center py-12 text-gray-500">Yükleniyor...</div>;
const Empty = ({ msg = "Henüz veri yok" }) => <div className="text-center py-12 text-gray-400">{msg}</div>;

export default AdminReports;
